import { CancelIcon, PlusIcon } from '@/assets/icons'
import LocationAutocomplete from '@components/LocationAutocomplete'
import { DEFAULT_EVENT_CATEGORY } from '@/constants/eventCategories'
import CategoryField from '@components/CategoryField'
import { usePlaceSearchContext } from '@hooks'
import { addLinkRow, removeLinkRow, setLinkRow } from '@lib/links'
import { placeSecondaryText } from '@lib/place'
import React from 'react'
import { useParams } from 'react-router-dom'
import { IdeaDraft, setLocationText, pickPlace } from '../ideaForm'
import {
    AddLinkButton,
    FormContainer,
    FormGroup,
    Input,
    Label,
    LinkInput,
    LinkInputRow,
    LinksContainer,
    LocationSecondaryText,
    RemoveLinkButton,
    TextArea,
} from './IdeaForm.styles'
import { iconSizes } from '@/theme/tokens'
import { TestIds } from '@/testIds'

interface IdeaFormProps {
	formId: string
	draft: IdeaDraft
	onDraftChange: (draft: IdeaDraft) => void
	onSubmit: (e: React.FormEvent) => void
	attachmentSlot?: React.ReactNode
}

const IdeaForm: React.FC<IdeaFormProps> = ({ formId, draft, onDraftChange, onSubmit, attachmentSlot }) => {
	const secondaryText = placeSecondaryText(draft.place)
	const { tripId } = useParams<{ tripId: string }>()
	// An idea has no day, so no destination: its search opens on the trip's places alone.
	const placeSearch = usePlaceSearchContext(tripId, { kind: 'idea' })

	return (
		<FormContainer id={formId} onSubmit={onSubmit}>
	<FormGroup>
		<Label htmlFor="idea-location">Location (Optional)</Label>
		<LocationAutocomplete
			id="idea-location"
			value={draft.place.text}
			onChange={text => onDraftChange(setLocationText(draft, text))}
			onPlaceSelect={place => onDraftChange(pickPlace(draft, place))}
			placeholder="Search for a location..."
			autoFocus
			context={placeSearch}
		/>
		{secondaryText && (
			<LocationSecondaryText>
				{secondaryText}
			</LocationSecondaryText>
		)}
	</FormGroup>

		<FormGroup>
			<Label htmlFor="idea-name">Name *</Label>
			<Input
				id="idea-name"
				data-testid={TestIds.idea.nameField}
				type="text"
				value={draft.name}
				onChange={e => onDraftChange({ ...draft, name: e.target.value })}
				placeholder="Enter idea name"
				autoComplete="off"
				data-1p-ignore
				required
			/>
		</FormGroup>

			<FormGroup>
				<Label htmlFor="idea-category">Category</Label>
				<CategoryField
					id="idea-category"
					aria-label="Category"
					value={draft.category || DEFAULT_EVENT_CATEGORY}
					onChange={category => onDraftChange({ ...draft, category })}
				/>
			</FormGroup>

			<FormGroup>
				<Label>Links</Label>
				{draft.links.length > 0 && (
					<LinksContainer>
						{draft.links.map((link, index) => (
							<LinkInputRow key={index}>
								<LinkInput
									data-testid={TestIds.idea.linkField}
									type="text"
									placeholder="Enter a link or any text (e.g., https://example.com)"
									value={link}
									onChange={e => onDraftChange({ ...draft, links: setLinkRow(draft.links, index, e.target.value) })}
									autoComplete="off"
								/>
								<RemoveLinkButton
									type="button"
									onClick={() => onDraftChange({ ...draft, links: removeLinkRow(draft.links, index) })}
									title="Remove link"
								>
									<CancelIcon sx={{ fontSize: iconSizes.md }} />
								</RemoveLinkButton>
							</LinkInputRow>
						))}
					</LinksContainer>
				)}
				<AddLinkButton
					type="button"
					data-testid={TestIds.idea.addLinkButton}
					onClick={() => onDraftChange({ ...draft, links: addLinkRow(draft.links) })}
				>
					<PlusIcon sx={{ fontSize: iconSizes.sm }} />
					Add Link
				</AddLinkButton>
			</FormGroup>

			<FormGroup>
				<Label htmlFor="idea-description">Description</Label>
				<TextArea
					id="idea-description"
					value={draft.description}
					onChange={e => onDraftChange({ ...draft, description: e.target.value })}
					placeholder="Add any notes or details about this idea"
					rows={4}
					autoComplete="off"
				/>
			</FormGroup>

			{attachmentSlot}
		</FormContainer>
	)
}

export default IdeaForm
